import { memo, useCallback, useState, type ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@ui/context-menu';
import { Pencil, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import type { GroupDetails } from '@/types/groups';
import { GroupDetailsDialog } from './GroupDetailsDialog';
import {
  addGroupSubscriberMutation,
  removeGroupSubscriberMutation,
} from '@/hooks/queries/folder';
import { useDeleteGroupMutation } from '@/hooks/queries/groups';
import { useConfirmHandler } from '@/hooks/useConfirmHandler';
// import { useUser } from '@/hooks/queries/auth';
// import { useGroupNotificationSubscription } from '@/hooks/useGroupNotificationSubscription';

const GROUP_ROOT_FOLDER_ID = '-1';

type GroupSidebarContextMenuProps = {
  group: GroupDetails;
  children: ReactNode;
  onRenameSelect: () => void;
};

/**
 * Right-click context menu for group rows in the groups sidebar drawer.
 * Supports delete (UI only), rename, add members, and notification subscribe toggle.
 */
function GroupSidebarContextMenu({
  group,
  children,
  onRenameSelect,
}: GroupSidebarContextMenuProps) {
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  // const user = useUser((state) => state.user);
  const groupId = String(group.id);

  // const { isSubscriber } = useGroupNotificationSubscription(groupId);
  const addSubscriberMutation = addGroupSubscriberMutation(GROUP_ROOT_FOLDER_ID, groupId);
  const removeSubscriberMutation = removeGroupSubscriberMutation(
    GROUP_ROOT_FOLDER_ID,
    groupId,
  );
  const { mutateAsync: deleteGroup, isPending: isDeletingGroup } = useDeleteGroupMutation();
  const confirm = useConfirmHandler();

  // const isSubscribePending =addSubscriberMutation.isPending || removeSubscriberMutation.isPending;

  const handleDeleteGroup = useCallback(async () => {
    if (!group.isAdmin || isDeletingGroup) return;

    const shouldDelete = await confirm({
      title: 'Delete group',
      description: `Are you sure you want to delete "${group.name}"? This action cannot be undone.`,
      variant: 'destructive',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!shouldDelete) return;

    try {
      const response = await deleteGroup(groupId);
      if (response.status) {
        toast.success(response.data.message || 'Group deleted successfully');
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to delete group';
      toast.error(message);
    }
  }, [confirm, deleteGroup, group.isAdmin, group.name, groupId, isDeletingGroup]);

  const handleOpenAddMembersDialog = useCallback(() => {
    setIsDetailsDialogOpen(true);
  }, []);

  // const handleToggleSubscription = useCallback(async () => {
  //   if (!user || isSubscribePending) return;

  //   const payload = {
  //     userId: String(user.id),
  //     groupId: Number(groupId),
  //   };

  //   try {
  //     if (isSubscriber) {
  //       await removeSubscriberMutation.mutateAsync(payload);
  //       toast.success('Unsubscribed from notifications');
  //     } else {
  //       await addSubscriberMutation.mutateAsync(payload);
  //       toast.success('Subscribed to notifications');
  //     }
  //   } catch (error: unknown) {
  //     const message =
  //       error instanceof Error && error.message
  //         ? error.message
  //         : 'Failed to update notification subscription';
  //     toast.error(message);
  //   }
  // }, [
  //   addSubscriberMutation,
  //   groupId,
  //   isSubscribePending,
  //   isSubscriber,
  //   removeSubscriberMutation,
  //   user,
  // ]);

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuItem
            onSelect={() => {
              void handleDeleteGroup();
            }}
            disabled={!group.isAdmin || isDeletingGroup}
            className="bg-error-E-main/10 hover:!bg-error-E-main/30 !text-error-E-dark flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={onRenameSelect}
            disabled={!group.isAdmin}
            className="flex items-center gap-2"
          >
            <Pencil className="h-4 w-4" />
            Rename
          </ContextMenuItem>
          <ContextMenuItem
            onSelect={handleOpenAddMembersDialog}
            className="flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Add Members
          </ContextMenuItem>
          {/* <ContextMenuItem
            onSelect={() => {
              void handleToggleSubscription();
            }}
            disabled={isSubscribePending || !user}
            className="flex items-center gap-2"
          >
            {isSubscriber ? (
              <BellOff className="h-4 w-4" />
            ) : (
              <Bell className="h-4 w-4" />
            )}
            {isSubscriber
              ? 'Unsubscribe from notifications'
              : 'Subscribe to notifications'}
          </ContextMenuItem> */}
        </ContextMenuContent>
      </ContextMenu>

      <GroupDetailsDialog
        group={group}
        open={isDetailsDialogOpen}
        onOpenChange={setIsDetailsDialogOpen}
      />
    </>
  );
}

export default memo(GroupSidebarContextMenu);
